/**
 * Shared Zoom-derived speaking and breakout-room statistics fields for Firestore.
 *
 * **Used in two products** (same numeric semantics; different parent documents):
 *
 * - **Code in Place (main webapp)** — merged into
 *   {@link SectionStatisticsDocumentSchema | section week statistics} at
 *   `course/{courseId}/sectionWeekStats/…`. Section docs add `sectionId`,
 *   `sectionWeekId`, optional percentiles, and `percentileCalculatedAt`.
 *
 * - **Teacher-feedback app** — merged into
 *   {@link TeacherFeedbackSessionStatsDocumentSchema | mentor session statistics}
 *   at `teacherFeedback/data/courses/…/mentors/…/sessions/…`. Those docs add
 *   mentor/session identity (`zoomUserId`, `sessionKey`, `meetingUuid`, etc.).
 *
 * Keep this module free of section- or teacher-feedback–specific keys so both
 * apps can depend on it without pulling unrelated shapes.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';

/** Breakout intervals in seconds relative to meeting start (same convention as section stats). */
export const SharedZoomSpeakingStatsBreakoutIntervalSchema = z.object({
  start: z.number(),
  end: z.number(),
});

/**
 * Core speaking + breakout stats produced from transcripts and Zoom webhook
 * events (VTT + breakout join/leave, etc.).
 */
export const SharedZoomSpeakingStatsFieldsSchema = z.object({
  studentTalkPercent: z.number(),
  studentTalkTime: z.number(),
  slTalkTime: z.number(),
  slTalkPercent: z.number(),
  breakoutRoomTime: z.number(),
  breakoutRoomPercent: z.number(),
  statsCalculatedAt: FirestoreTimestampSchema,
  breakoutRoomIntervals: z
    .array(SharedZoomSpeakingStatsBreakoutIntervalSchema)
    .optional(),
});

export type SharedZoomSpeakingStatsFields = z.infer<
  typeof SharedZoomSpeakingStatsFieldsSchema
>;
